// src/App.js
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import TrendModal from './TrendModal';
import regionMap from './utils/regionMap';
import Ticker from './components/Ticker';
import Pager from './components/Pager';
import PillSelect from './components/PillSelect';
import RegionPillSelect from './components/RegionPillSelect';

const REGION_STORAGE_KEY = 'popdaq:region';
const VIEW_STORAGE_KEY = 'popdaq:view'; // 'recommended' | 'bestseller'
const PAGE_SIZE = 20;
const TOP_N = 10;

/* --------------------------- helpers / URL state --------------------------- */
function parseInitialStateFromURL() {
  try {
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);

    let region = 'global';
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 1 && parts[0].toLowerCase() === 'global') region = 'global';
    else if (parts.length === 2 && parts[0].toLowerCase() === 'region') region = parts[1].toLowerCase();

    const qView = (params.get('v') || params.get('view') || '').toLowerCase();
    const view = qView === 'bestseller' ? 'bestseller' : 'recommended';
    return { region, view };
  } catch {
    return { region: 'global', view: 'recommended' };
  }
}
function readInitialState() {
  const url = parseInitialStateFromURL();
  const savedReg = localStorage.getItem(REGION_STORAGE_KEY) || '';
  const savedView = localStorage.getItem(VIEW_STORAGE_KEY) || '';
  const region = url.region || savedReg || 'global';
  const view = url.view || (savedView === 'bestseller' ? 'bestseller' : 'recommended');
  return { region, view };
}
function pushURL(region, view) {
  try {
    const isGlobal = !region || region === 'global';
    const path = isGlobal ? '/global' : `/region/${region}`;
    const params = new URLSearchParams();
    if (view === 'bestseller' && !isGlobal) params.set('v', 'bestseller');
    const q = params.toString();
    const url = q ? `${path}?${q}` : path;
    window.history.replaceState({}, '', url);
  } catch {}
}

const normalizeId = (id) => id?.trim().toLowerCase() || '';
const getPriceValue = (str) => Number(String(str || '').replace(/[^0-9.]/g, '')) || 0;

/* rank helpers */
function computeDisplayRank(isGlobal, usingBestSellers, item) {
  if (isGlobal) return item.globalRank ?? Infinity;
  if (usingBestSellers) return item.rank ?? item.bestSellingRank ?? Infinity;
  return item.rank ?? item.bestSellingRank ?? Infinity;
}
function getHistoryFor(p, historicalData) {
  const key = normalizeId(p?.productId);
  return (historicalData && key && historicalData[key]) || [];
}
function getGlobalChange(p, historicalData) {
  if (typeof p?.globalRankChange === 'number') return p.globalRankChange;
  const h = getHistoryFor(p, historicalData);
  if (h.length < 2) return 0;
  const prev = h[h.length - 2]?.globalRank;
  const curr = h[h.length - 1]?.globalRank;
  if (prev == null || curr == null) return 0;
  return prev - curr; // + = moved up toward #1
}
function getChangeWithPercent(p, { isGlobal, usingBestSellers, historicalData }) {
  const curr = computeDisplayRank(isGlobal, usingBestSellers, p);
  if (!Number.isFinite(curr)) return { change: 0, prev: null, pct: null };

  const change = isGlobal
    ? getGlobalChange(p, historicalData)
    : (typeof p?.rankChange === 'number' ? p.rankChange : 0);

  if (typeof change === 'number' && change !== 0) {
    const prev = curr + change;
    if (prev > 0 && Number.isFinite(prev)) {
      const pct = (Math.abs(change) / prev) * 100;
      return { change, prev, pct };
    }
  }
  return { change: 0, prev: null, pct: null };
}

/* ------------------------------- tiny hooks -------------------------------- */
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.matchMedia
    ? window.matchMedia(`(max-width:${breakpoint - 1}px)`).matches
    : window.innerWidth < breakpoint);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
    const on = (e) => setMobile(e.matches);
    mq.addEventListener?.('change', on);
    mq.addListener?.(on);
    return () => {
      mq.removeEventListener?.('change', on);
      mq.removeListener?.(on);
    };
  }, [breakpoint]);

  return mobile;
}

/* ------------------------------ InfoDot (portal) --------------------------- */
function InfoDot() {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 320, placement: 'top' });

  const show = () => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const maxW = Math.min(480, window.innerWidth - 24);
    const left = Math.min(Math.max(12, r.left + r.width / 2 - maxW / 2), window.innerWidth - maxW - 12);
    setPos({ left, top: r.top, width: maxW, placement: 'top' });
    setOpen(true);

    requestAnimationFrame(() => {
      const bubble = document.getElementById('infodot-bubble'); if (!bubble) return;
      const h = bubble.offsetHeight;
      let top = r.top - 12 - h; let placement = 'top';
      if (top < 8) { top = r.bottom + 12; placement = 'bottom'; }
      setPos((prev) => ({ ...prev, top, placement }));
    });
  };
  const hide = () => setOpen(false);

  return (
    <>
      <span
        ref={ref}
        className="infoDot"
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-label="What does movement mean?"
      >
        i
      </span>

      {open && createPortal(
        <div
          id="infodot-bubble"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            width: pos.width,
            maxWidth: pos.width,
            background: '#2f2f2f',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 10,
            fontSize: 11,
            lineHeight: 1.35,
            boxShadow: '0 8px 22px rgba(0,0,0,.28)',
            zIndex: 2147483647,
            pointerEvents: 'none',
          }}
        >
          Movement = change in rank since the previous snapshot. Positive means moved UP toward #1; negative means down.
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              border: '6px solid transparent',
              ...(pos.placement === 'top'
                ? { bottom: -6, borderTopColor: '#2f2f2f' }
                : { top: -6, borderBottomColor: '#2f2f2f' }),
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
}

/* ------------------------------ Polaroid Card ----------------------------- */
function PolaroidCard({ product, rank, rankChange, onClick, compact = false }) {
  const price = product?.price || '';
  const status = (product?.status || '').trim();
  const brand = product?.brand || 'POP MART';
  const name = product?.name || '';
  const img = product?.image;

  const supportsHover = useMemo(() => {
    try {
      return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch { return false; }
  }, []);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, active: false });

  const onMove = (e) => {
    if (!supportsHover) return;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const ry = (px - 0.5) * 10;
    const rx = -(py - 0.5) * 10;
    setTilt({ rx, ry, active: true });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0, active: false });
  const onFocus = () => setTilt({ rx: 0, ry: 0, active: true });
  const onBlur = () => setTilt({ rx: 0, ry: 0, active: false });

  const transform = supportsHover && tilt.active
    ? `perspective(900px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateY(-6px) scale(1.02)`
    : undefined;
  const shadow = tilt.active
    ? '0 14px 32px rgba(0,0,0,.14)'
    : '0 1px 2px rgba(0,0,0,0.06)';

  let changeLabel = '—';
  let changeClass = 'flat';
  if (typeof rankChange === 'number') {
    if (rankChange > 0) { changeLabel = `↑ ${rankChange}`; changeClass = 'up'; }
    else if (rankChange < 0) { changeLabel = `↓ ${Math.abs(rankChange)}`; changeClass = 'down'; }
    else { changeLabel = '±0'; }
  }

  return (
    <button
      onClick={() => onClick?.(product)}
      title={name}
      className={`pola ${compact ? 'pola--compact' : ''}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onMouseEnter={() => setTilt(t => ({ ...t, active: true }))}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{ ...polaStyle.container, transform, boxShadow: shadow, willChange: 'transform' }}
    >
      <div style={polaStyle.badgeLayer}>
        {status && (
          <span style={polaStyle.statusPill} title={status}>
            <span style={polaStyle.statusText}>{status}</span>
          </span>
        )}
        <span style={polaStyle.rankChip}>#{rank}</span>
      </div>

      <div style={polaStyle.imgWrap}>
        {img ? <img src={img} alt="" loading="lazy" style={polaStyle.img(compact)} /> : <div style={polaStyle.imgPh} />}
      </div>

      <div style={polaStyle.caption}>
        <div style={polaStyle.brand}>{brand}</div>
        <div style={polaStyle.title}>{name}</div>

        <div style={polaStyle.meta}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <div style={polaStyle.change(changeClass)}>{changeLabel}</div>
            <InfoDot />
          </span>
          <div style={polaStyle.price}>{price}</div>
        </div>
      </div>
    </button>
  );
}

const polaStyle = {
  container: {
    position: 'relative',
    width: '100%',
    textAlign: 'left',
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    transition: 'box-shadow .2s ease, transform .2s ease, border-color .2s ease'
  },
  badgeLayer: { position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, pointerEvents: 'none' },
  statusPill: {
    pointerEvents: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    borderRadius: 999,
    background: '#111',
    color: '#fff',
    whiteSpace: 'nowrap',
    maxWidth: 'calc(100% - 64px)',
    boxShadow: '0 1px 2px rgba(0,0,0,.12)'
  },
  statusText: { display: 'block', minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rankChip: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontWeight: 800,
    fontSize: 12,
    background: 'rgba(0,0,0,.85)',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    pointerEvents: 'auto'
  },
  imgWrap: { background: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1 / 1' },
  img: (compact) => ({ width: compact ? '70%' : '78%', height: 'auto', objectFit: 'contain' }),
  imgPh: { width: '78%', height: '78%', background: '#e9e9e9', borderRadius: 12 },
  caption: { padding: '10px 12px 12px' },
  brand: { fontSize: 11, letterSpacing: '.02em', color: '#666', marginBottom: 4 },
  title: { fontSize: 13, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  meta: { marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  price: { fontWeight: 600, fontSize: 12 },
  change: (kind) => ({
    fontWeight: 700, fontSize: 12, padding: '2px 8px', borderRadius: 999,
    color: kind === 'up' ? '#0a7b34' : kind === 'down' ? '#b00020' : '#666',
    background: kind === 'up' ? '#e8f6ee' : kind === 'down' ? '#fde8eb' : '#f1f1f1',
  }),
};

/* -------------------------------- Carousel -------------------------------- */
function Top10Carousel({ items, onItemClick, getRankChange }) {
  if (!items?.length) return null;
  const trackRef = useRef(null);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const first = el.firstElementChild;
    const width = first ? first.getBoundingClientRect().width : 260;
    const delta = width || 260;
    el.scrollBy({ left: dir * (delta + 16), behavior: 'smooth' });
  };

  return (
    <section className="gridSection" style={{ marginBottom: 20 }}>
      <h2 className="sectionTitle">Top 10</h2>
      <div className="carouselViewport">
        <div ref={trackRef} className="carouselTrack">
          {items.map((p, i) => (
            <div key={p.productId || i} className="carouselCell">
              <PolaroidCard
                product={p}
                rank={i + 1}
                rankChange={getRankChange?.(p)}
                onClick={onItemClick}
                compact
              />
            </div>
          ))}
        </div>
        <button className="carouselArrow left" onClick={() => scrollByCard(-1)} aria-label="Scroll left">‹</button>
        <button className="carouselArrow right" onClick={() => scrollByCard(1)} aria-label="Scroll right">›</button>
      </div>
    </section>
  );
}

/* ---------------------------- Grid (+optional IO) -------------------------- */
function PolaroidGrid({ items, startRank, onItemClick, getRankChange, batch = 20, infinite = true }) {
  const [visible, setVisible] = useState(infinite ? batch : items.length);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => { setVisible(infinite ? batch : items.length); }, [items, batch, infinite]);

  useEffect(() => {
    if (!infinite || !sentinelRef.current) return;
    let locked = false;
    const io = new IntersectionObserver((entries) => {
      const [e] = entries;
      if (e.isIntersecting && !locked && visible < items.length) {
        locked = true;
        setLoading(true);
        setTimeout(() => {
          setVisible(v => Math.min(v + batch, items.length));
          setLoading(false);
          locked = false;
        }, 300);
      }
    }, { rootMargin: '250px 0px' });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [infinite, items.length, visible, batch]);

  const slice = infinite ? items.slice(0, visible) : items;

  return (
    <>
      <div className="polaroidGrid">
        {slice.map((item, idx) => {
          const rankChange = getRankChange?.(item);
          const displayRank = (startRank ?? 1) + idx;
          return (
            <div
              key={item.productId || idx}
              className="polaroidCell"
              data-pid={normalizeId(item.productId)}
            >
              <PolaroidCard
                product={item}
                rank={displayRank}
                rankChange={rankChange}
                onClick={() => onItemClick?.({ ...item, productId: normalizeId(item.productId) })}
                compact
              />
            </div>
          );
        })}
      </div>

      {infinite && <div ref={sentinelRef} style={{ height: 1 }} />}
      {infinite && loading && (
        <div className="loaderHUD">
          <span>Loading products</span>
          <span className="loaderDots">…</span>
        </div>
      )}
    </>
  );
}

/* ---------------------------------- App ----------------------------------- */
export default function App() {
  const boot = useMemo(readInitialState, []);
  const [region, setRegion] = useState(boot.region);
  const [viewMode, setViewMode] = useState(boot.view);

  const [products, setProducts] = useState([]);
  const [productsBestSellers, setProductsBS] = useState([]);
  const [productsGlobal, setProductsGlobal] = useState([]);
  const [historicalData, setHistoricalData] = useState(null);
  const [bestSellersHistoricalData, setBSHist] = useState(null);

  const [filter, setFilter] = useState('all');
  const [sortOption, setSortOption] = useState('rank-asc');
  const [modalProduct, setModalProduct] = useState(null);

  const [page, setPage] = useState(1);

  const scrollYRef = useRef(0);
  const anchorIdRef = useRef(null);

  const isMobile = useIsMobile(768);

  // SPA home click (fade only)
  const navigate = useNavigate();
  const location = useLocation();
  const [isFading, setIsFading] = useState(false);

  useLayoutEffect(() => {
    const setVw = () =>
      document.documentElement.style.setProperty('--vw', `${window.innerWidth}px`);
    setVw();
    window.addEventListener('resize', setVw);
    return () => window.removeEventListener('resize', setVw);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const prevOverflowX = root.style.overflowX;
    root.style.overflowX = 'clip';
    return () => { root.style.overflowX = prevOverflowX; };
  }, []);

  useLayoutEffect(() => {
    const el = document.querySelector('.topbar');
    if (!el) return;
    const set = () =>
      document.documentElement.style.setProperty('--topbar-height', `${el.offsetHeight}px`);
    set(); window.addEventListener('resize', set);
    return () => window.removeEventListener('resize', set);
  }, []);

  useEffect(() => {
    localStorage.setItem(REGION_STORAGE_KEY, region);
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    pushURL(region, viewMode);
  }, [region, viewMode]);

  /* data */
  useEffect(() => {
    fetch('/data/historical-trends.json').then(r => r.json()).then(setHistoricalData).catch(() => {});
    fetch('/data/best-sellers/historical-trends.json').then(r => r.json()).then(setBSHist).catch(() => {});
  }, []);
  useEffect(() => {
    Promise.all([
      fetch('/data/latest-global.json').then(r => r.json()),
      fetch('/data/global/latest-global-with-change.json').then(r => r.json()).catch(() => [])
    ]).then(([baseData, changeData]) => {
      const changeMap = {};
      (changeData || []).forEach(item => { const key = normalizeId(item.productId); if (key) changeMap[key] = item; });
      const normalized = (baseData || []).map(item => ({ ...item, productId: normalizeId(item.productId) }));
      const mergedForGlobal = normalized.map(item => ({ ...item, ...(changeMap[item.productId] || {}) }));
      setProductsGlobal(mergedForGlobal);
    }).catch(() => {});
  }, [region]);
  useEffect(() => {
    if (region === 'global') return;
    fetch(`/data/by-region/latest-${region}.json`).then(r => r.json()).then(d => {
      const norm = (d || []).map(x => ({ ...x, productId: normalizeId(x.productId) }));
      setProducts(norm);
    }).catch(() => {});
  }, [region]);
  useEffect(() => {
    if (region === 'global') return;
    fetch(`/data/best-sellers/by-region/latest-${region}.json`).then(r => r.json()).then(d => {
      const norm = (d || []).map(x => ({ ...x, productId: normalizeId(x.productId) }));
      setProductsBS(norm);
    }).catch(() => setProductsBS([]));
  }, [region]);

  useEffect(() => { setPage(1); }, [region, viewMode, filter, sortOption, isMobile]);

  /* scroll lock + restore to clicked card */
  useEffect(() => {
    if (modalProduct) {
      scrollYRef.current = window.scrollY || window.pageYOffset || 0;
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
      document.body.style.overflow = 'hidden';
    } else {
      const y = scrollYRef.current || 0;
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      const anchorId = anchorIdRef.current;
      const restore = () => {
        const prev = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        if (anchorId) {
          const el = document.querySelector(`[data-pid="${anchorId}"]`);
          if (el) el.scrollIntoView({ block: 'center', inline: 'nearest' });
          anchorIdRef.current = null;
        } else {
          window.scrollTo(0, y);
        }
        document.documentElement.style.scrollBehavior = prev || '';
      };
      requestAnimationFrame(restore);
      setTimeout(restore, 0);
    }
  }, [modalProduct]);

  /* derive view */
  const isGlobal = region === 'global';
  const usingBestSellers = !isGlobal && viewMode === 'bestseller';
  const rawList = usingBestSellers ? productsBestSellers : (isGlobal ? productsGlobal : products);
  const filtered = rawList.filter(item => filter === 'all' || (item.status || '').toLowerCase() === filter);

  const sorted = [...filtered].sort((a, b) => {
    switch (sortOption) {
      case 'rank-change-desc': return isGlobal ? (b.globalRankChange || 0) - (a.globalRankChange || 0) : (b.rankChange || 0) - (a.rankChange || 0);
      case 'rank-change-asc': return isGlobal ? (a.globalRankChange || 0) - (b.globalRankChange || 0) : (a.rankChange || 0) - (b.rankChange || 0);
      case 'rank-asc': return computeDisplayRank(isGlobal, usingBestSellers, a) - computeDisplayRank(isGlobal, usingBestSellers, b);
      case 'rank-desc': return computeDisplayRank(isGlobal, usingBestSellers, b) - computeDisplayRank(isGlobal, usingBestSellers, a);
      case 'price-desc': return getPriceValue(b.price) - getPriceValue(a.price);
      case 'price-asc': return getPriceValue(a.price) - getPriceValue(b.price);
      case 'brand-az': return (a.brand ?? '').localeCompare(b.brand ?? '');
      case 'name-az': return (a.name ?? '').localeCompare(b.name ?? '');
      default: return 0;
    }
  });

  const rankOrdered = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => computeDisplayRank(isGlobal, usingBestSellers, a) - computeDisplayRank(isGlobal, usingBestSellers, b));
    return list;
  }, [filtered, isGlobal, usingBestSellers]);

  const top10 = rankOrdered.slice(0, TOP_N);
  const top10Ids = new Set(top10.map(p => normalizeId(p.productId)));
  const restForGrid = sorted.filter(p => !top10Ids.has(normalizeId(p.productId)));

  const pageCount = Math.max(1, Math.ceil(restForGrid.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = restForGrid.slice(startIndex, startIndex + PAGE_SIZE);
  const pageStartRank = TOP_N + 1 + startIndex;

  const openModal = (item) => {
    anchorIdRef.current = normalizeId(item.productId);
    setModalProduct({ ...item, productId: anchorIdRef.current });
  };
  const closeModal = () => setModalProduct(null);

  const openDetailPage = (item) => {
    const pid = normalizeId(item.productId);
    const params = new URLSearchParams();
    if (region && region !== 'global') params.set('region', region);
    if (!isGlobal && viewMode === 'bestseller') params.set('v', 'bestseller');
    const q = params.toString();
    const url = q ? `/product/${encodeURIComponent(pid)}?${q}` : `/product/${encodeURIComponent(pid)}`;
    window.location.assign(url);
  };

  const changeGetter = (p) => (isGlobal ? getGlobalChange(p, historicalData) : (p.rankChange ?? 0));
  const changeWithPct = (item) =>
    getChangeWithPercent(item, { isGlobal, usingBestSellers, historicalData: viewMode === 'bestseller' ? bestSellersHistoricalData : historicalData });

  const renderTicker = () => (
    <Ticker
        items={sorted}
        speedPxPerSec={80}
        storageKey={`popdaq:ticker:${region}:${viewMode}`}
        renderItem={(item) => {
        const { change, pct } = changeWithPct(item);
        const symbol = change > 0 ? '🔺' : change < 0 ? '🔻' : '⏸️';
        const abs = Number.isFinite(change) ? Math.abs(change) : 0;
        return (
          <button
            type="button"
            className={`ticker-chip ${change > 0 ? 'up' : change < 0 ? 'down' : 'steady'}`}
            onClick={() => openDetailPage(item)}
            title="Open full detail page"
          >
            {item.name ?? 'Unnamed'} — {symbol} {abs}
            {Number.isFinite(pct) ? ` (${change > 0 ? '+' : change < 0 ? '-' : ''}${pct.toFixed(1)}%)` : ''}
          </button>
        );
      }}
    />
  );

  const filterItems = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'up', label: 'Up' },
    { value: 'down', label: 'Down' },
    { value: 'removed', label: 'Removed' },
  ];
  const filterLabel = filterItems.find(f => f.value === filter)?.label || 'All';

  const regionItems = [
    { value: 'global', label: 'Global' },
    ...Object.entries(regionMap).map(([code, name]) => ({ value: code, label: name })),
  ];
  const regionLabel = regionItems.find(r => r.value === region)?.label || 'Global';

  const viewItems = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'bestseller', label: 'Best Sellers' },
  ];
  const viewLabel = viewItems.find(v => v.value === viewMode)?.label || 'Recommended';

  const sortItems = [
    { value: 'rank-change-desc', label: 'Rank Δ (high → low)' },
    { value: 'rank-change-asc', label: 'Rank Δ (low → high)' },
    { value: 'rank-asc', label: 'Rank (low → high)' },
    { value: 'rank-desc', label: 'Rank (high → low)' },
    { value: 'price-desc', label: 'Price (high → low)' },
    { value: 'price-asc', label: 'Price (low → high)' },
    { value: 'brand-az', label: 'Brand A–Z' },
    { value: 'name-az', label: 'Name A–Z' },
  ];
  const buildSortLabel = (v) => (sortItems.find(s => s.value === v)?.label || 'Sort');
  const sortLabel = buildSortLabel(sortOption);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const goHomeSleek = (e) => {
    if (e) e.preventDefault();
    if (modalProduct) setModalProduct(null);

    const curr = (location.pathname || '').replace(/\/+$/, '');
    const alreadyGlobal = curr === '/global';

    setIsFading(true);

    window.setTimeout(() => {
      setRegion('global');
      setViewMode('recommended');
      setFilter('all');
      setSortOption('rank-asc');
      setPage(1);

      try { window.history.replaceState({}, '', '/global'); } catch {}

      if (!alreadyGlobal) {
        navigate('/global', { replace: false });
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.setTimeout(() => setIsFading(false), 30);
    }, 120);
  };

  return (
    <div className={`appFade ${isFading ? 'isFading' : ''}`}>
      <header className="topbar">
        <a
          href="/global"
          onClick={goHomeSleek}
          className="logoLink"
          aria-label="POPDAQ Home"
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', pointerEvents: 'auto' }}
        >
          <img src="/directory/popdaqArtboard1@4x.png" alt="POPDAQ" className="logo" />
        </a>

        <div className="tickerShell">{renderTicker()}</div>
      </header>

      <div className="pageGutter">
        <div className="pillBar">
          <PillSelect label={filterLabel} items={filterItems} value={filter} onChange={setFilter} />

          <RegionPillSelect
            label={regionLabel}
            items={regionItems}
            value={region}
            onChange={(v) => { setRegion(v); if (v === 'global') setViewMode('recommended'); }}
          />

          {region !== 'global' && (
            <PillSelect label={viewLabel} items={viewItems} value={viewMode} onChange={setViewMode} />
          )}

          <PillSelect label={sortLabel} items={sortItems} value={sortOption} onChange={setSortOption} />
        </div>

        <p className="count">Showing {sorted.length} products</p>

        {isMobile && <Top10Carousel items={top10} onItemClick={openModal} getRankChange={changeGetter} />}
        {!isMobile && page === 1 && <Top10Carousel items={top10} onItemClick={openModal} getRankChange={changeGetter} />}

        {isMobile ? (
          <PolaroidGrid
            items={restForGrid}
            startRank={TOP_N + 1}
            batch={PAGE_SIZE}
            infinite
            onItemClick={openModal}
            getRankChange={changeGetter}
          />
        ) : (
          <>
            <PolaroidGrid
              items={pageItems}
              startRank={pageStartRank}
              batch={PAGE_SIZE}
              infinite={false}
              onItemClick={openModal}
              getRankChange={changeGetter}
            />
            <Pager page={page} total={pageCount} onChange={handlePageChange} />
          </>
        )}
      </div>

      {modalProduct && (
        <TrendModal
          product={modalProduct}
          globalData={productsGlobal}
          historicalData={viewMode === 'bestseller' ? bestSellersHistoricalData : historicalData}
          currentRegion={region}
          viewMode={viewMode}
          onClose={closeModal}
        />
      )}

      <footer className="footer"><p>Powered by POPDAQ</p></footer>

      {/* Inline CSS kept from the working version (unchanged layout); updated to match the polished pill/search design */}
      <style>{`
        :root { --ui-font: Inter, "Helvetica Neue", Arial, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto; }
        body { font-family: var(--ui-font); }
        html, body, #root { max-width: 100%; overflow-x: hidden; }
        .tickerShell { width: 100%; max-width: var(--vw, 100vw); overflow: hidden; }
        .topbar { border-bottom: 1px solid rgba(0,0,0,.08); }

        .pageGutter { max-width: 1400px; margin: 0 auto; padding: 0 16px; }
        @media (min-width:640px){ .pageGutter { padding: 0 24px; } }
        @media (min-width:1024px){ .pageGutter { padding: 0 32px; } }
        @media (min-width:1536px){ .pageGutter { padding: 0 48px; } }

        .sectionTitle { font-size:16px; font-weight:700; margin:12px 0 10px; }

        .pillBar{
          display:flex; align-items:center; justify-content:center;
          gap:12px; padding:14px 0 12px; flex-wrap:wrap;
        }
        .pillWrap{ position:relative; }
        .pill{
          display:inline-flex; align-items:center; gap:8px;
          height:38px; padding:0 16px; border-radius:999px;
          background:linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
          border:1px solid #d8deeb; cursor:pointer;
          font-weight:700; font-size:14px; color:#0f172a;
          box-shadow:0 6px 20px rgba(15,23,42,0.08);
          transition:background .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .pill:hover{ background:linear-gradient(135deg, #fdfdff 0%, #eef2fb 100%); border-color:#cfd7e8; box-shadow:0 10px 26px rgba(15,23,42,0.12); }
        .pill--open{ background:linear-gradient(135deg, #eef2fb 0%, #e7edf8 100%); border-color:#b7c3dd; box-shadow:0 14px 32px rgba(15,23,42,0.16); transform:translateY(-1px); }
        .pillLabel{ transform:translateY(.5px); letter-spacing:-0.01em; }
        .pillCaret{ font-size:12px; opacity:.7; margin-left:2px; }

        .pillBackdrop{ position:fixed; inset:0; z-index:1000; background:transparent; }

        .pillPanel{
          position:fixed; z-index:1001;
          transform: translateY(-6px); opacity:0; animation: pillIn .16s ease forwards;
          background:rgba(255,255,255,0.96);
          backdrop-filter: blur(12px);
          border:1px solid rgba(204,210,225,.8);
          border-radius:16px; box-shadow:0 22px 60px rgba(15,23,42,0.18);
          padding:10px; min-width:260px;
          display:flex; flex-direction:column; gap:6px;
        }
        @keyframes pillIn{ to { transform:none; opacity:1; } }

        .pillPanelHeader{ padding:6px 8px 4px; border-bottom:1px solid #e9edf5; display:flex; flex-direction:column; gap:4px; }
        .pillPanelTitle{ font-weight:700; font-size:13px; color:#0f172a; letter-spacing:-0.01em; }
        .pillPanelHint{ font-size:12px; color:#64748b; }
        .pillPanel--region .pillPanelHeader{ gap:8px; }

        .pillSearchWrap{ display:flex; align-items:center; gap:8px; background:#f5f7fb; border:1px solid #e3e8f2; border-radius:12px; padding:8px 10px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.6); }
        .pillSearchIcon{ font-size:13px; opacity:.7; }
        .pillSearch{ flex:1; border:none; background:transparent; outline:none; font-size:13px; color:#0f172a; }
        .pillSearch::placeholder{ color:#94a3b8; }

        .pillList{ display:flex; flex-direction:column; gap:6px; padding:4px 2px 2px; }
        .pillItem{
          width:100%; display:flex; align-items:center; gap:12px;
          padding:11px 12px; border-radius:12px; cursor:pointer; text-align:left;
          font-weight:600; font-size:14px; color:#0f172a;
          transition: background .14s ease, box-shadow .14s ease, border-color .14s ease, transform .14s ease;
          border:1px solid #e7ebf4; background:#fff;
          box-shadow:0 1px 0 rgba(255,255,255,0.8), 0 1px 6px rgba(15,23,42,0.04);
        }
        .pillItem:hover{ background:#f6f8fc; border-color:#d8deeb; box-shadow:0 1px 0 rgba(255,255,255,0.9), 0 6px 18px rgba(15,23,42,0.08); transform:translateY(-1px); }
        .pillItem--active{ border-color:#1f2937; background:linear-gradient(135deg, #f1f4ff 0%, #e9eeff 100%); box-shadow:0 10px 26px rgba(15,23,42,0.12); }
        .pillItemText{ transform:translateY(.5px); }
        .pillEmpty{ padding:14px 10px; text-align:center; color:#94a3b8; font-weight:600; }

        .pillCheck{ width:20px; height:20px; border-radius:8px; border:2px solid #cfd5dd; background:#f8fafc; display:inline-flex; align-items:center; justify-content:center; transition: all .16s ease; box-shadow: inset 0 1px 0 rgba(255,255,255,.6); }
        .pillCheckDot{ width:8px; height:8px; border-radius:6px; background:transparent; transition: transform .16s ease, background .16s ease; transform:scale(.5); }
        .pillCheck--on{ border-color:#111827; background:#111827; box-shadow:0 6px 16px rgba(17,24,39,0.3); }
        .pillCheck--on .pillCheckDot{ background:#fff; transform:scale(1); }

        .carouselViewport { position:relative; }
        .carouselTrack { display:grid; grid-auto-flow:column; gap:16px; overflow-x:auto; padding:2px 2px 8px;
                         scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch; grid-auto-columns: 100%; }
        .carouselCell { scroll-snap-align:start; }
        @media (min-width:640px){ .carouselTrack { grid-auto-columns: calc((100% - 16px)/2); } }
        @media (min-width:1024px){ .carouselTrack { grid-auto-columns: calc((100% - 2*16px)/3); } }
        @media (min-width:1280px){ .carouselTrack { grid-auto-columns: calc((100% - 3*16px)/4); } }

        .carouselArrow { position:absolute; top:45%; transform:translateY(-50%); border:1px solid rgba(0,0,0,.2);
                         background:rgba(255,255,255,.96); height:36px; width:36px; border-radius:999px; display:inline-flex; align-items:center;
                         justify-content:center; font-size:18px; cursor:pointer; box-shadow:0 4px 12px rgba(0,0,0,.12); z-index:10; }
        .carouselArrow.left{ left:6px; } .carouselArrow.right{ right:6px; }

        .polaroidGrid { display:grid; gap:16px; grid-template-columns: repeat(2, 1fr); }
        @media (min-width:640px){ .polaroidGrid { grid-template-columns: 1fr 1fr; } }
        @media (min-width:1024px){ .polaroidGrid { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width:1280px){ .polaroidGrid { grid-template-columns: repeat(4, 1fr); } }

        .polaroidCell { display:flex; }
        .pola { display:flex; flex-direction:column; height:100%; }

        .loaderHUD { display:flex; justify-content:center; gap:6px; color:#9a9a9a; font-size:14px; padding:18px 0 28px; }
        @keyframes dotPulse { 0%{opacity:.2;} 50%{opacity:1;} 100%{opacity:.2;} }
        .loaderDots { animation: dotPulse 1.2s infinite ease-in-out; }

        .infoDot{ display:inline-flex; align-items:center; justify-content:center; width:12px; height:12px; font-size:9px; line-height:1;
                  border-radius:999px; border:1px solid #cfd3d9; color:#6b7280; background:#fff; box-shadow:0 1px 1px rgba(0,0,0,.04);
                  cursor:help; user-select:none; }

        .pager { display:flex; align-items:center; justify-content:center; gap:10px; padding:24px 0 40px; }
        .pagerBtn, .pagerArrow { min-width:34px; height:34px; padding:0 10px; border-radius:10px; border:1px solid rgba(0,0,0,.15);
                                  background:#fff; font-weight:600; font-size:14px; cursor:pointer; }
        .pagerBtn.active { background:#111; color:#fff; border-color:#111; }
        .pagerArrow:disabled { opacity:.35; cursor:default; }
        .pagerEllipsis { color:#aaa; padding:0 4px; letter-spacing:2px; }
      `}</style>
    </div>
  );
}
